/*
 * @Author: Ryan
 * @Date: 2025-12-02
 * @Description: 视频展示区，演示AI视频翻译跨语言功能，支持视频播放和静音切换
 */
import Image from 'next/image';
import React, { useCallback, useRef, useState } from 'react';

import SoundToggle from './SoundToggle';
import StartButton from './StartButton';

interface VideoData {
  image: string;
  flagIcon: string;
  flagAlt: string;
  videoUrl: string;
}

interface VideoCardProps {
  video: VideoData;
  index: number;
  isPlaying: boolean;
  isMuted: boolean;
  onPlayClick: () => void;
  onSoundToggle: () => void;
}

const VideoCard: React.FC<VideoCardProps> = ({
  video,
  index,
  isPlaying,
  isMuted,
  onPlayClick,
  onSoundToggle,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // 当播放状态改变时，控制视频播放
  React.useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (isPlaying) {
      videoElement.play().catch((error) => {
        console.warn('视频播放失败:', error);
      });
    } else {
      videoElement.pause();
      videoElement.currentTime = 0;
    }
  }, [isPlaying]);

  // 更新静音状态（不重新加载视频，避免闪动）
  React.useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.muted = isMuted;
    }
  }, [isMuted]);

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-lg shadow-blue-200/50">
      {/* 视频元素 - 使用 poster 作为封面，防止闪动 */}
      <video
        ref={videoRef}
        className="w-full h-auto object-cover"
        src={video.videoUrl}
        poster={video.image}
        playsInline
        loop
        muted={isMuted}
        preload="metadata"
      />

      {/* 左下角：国旗图标 + 静音切换按钮 */}
      <div className="absolute bottom-4 left-4 flex items-center gap-2 z-9 bg-white rounded-full ">
        {/* 国旗图标 - 点击播放视频 */}
        <button
          type="button"
          onClick={onPlayClick}
          className="relative w-[34px] h-[34px] rounded-full overflow-hidden hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label={isPlaying ? '停止播放' : '播放视频'}
        >
          <Image
            src={video.flagIcon}
            alt={video.flagAlt}
            fill
            className="object-cover"
            loading="lazy"
          />
        </button>

        {/* 静音切换按钮 - 与国旗并排 */}
        <SoundToggle isMuted={isMuted} onToggle={onSoundToggle} />
      </div>
    </div>
  );
};

const VideoShowcaseSection: React.FC = () => {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  // 每个视频独立的静音状态，默认都是静音
  const [mutedStates, setMutedStates] = useState<boolean[]>([true, true, true]);

  const videos: VideoData[] = [
    {
      image: '/images/video-translator-english-section.jpeg',
      flagIcon: '/images/country/flag-us.png',
      flagAlt: 'English (US)',
      videoUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/breaklanguagebarriersen_1779611598513283072.mp4',
    },
    {
      image: '/images/video-translator-french-section.jpeg',
      flagIcon: '/images/country/flag-fr.png',
      flagAlt: 'French',
      videoUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/breaklanguagebarriersfr_1779611598521667584.mp4',
    },
    {
      image: '/images/video-translator-german-section.jpeg',
      flagIcon: '/images/country/flag-de.png',
      flagAlt: 'German',
      videoUrl:
        'https://assets.falcocut.ai/landing-page/video-translator/breaklanguagebarriersde_1779611657162231808.mp4',
    },
  ];

  // 处理视频播放点击
  const handlePlayClick = useCallback(
    (index: number) => {
      if (playingIndex === index) {
        // 如果点击的是当前播放的视频，则停止播放
        setPlayingIndex(null);
      } else {
        // 否则，播放点击的视频（其他视频会自动停止）
        setPlayingIndex(index);
      }
    },
    [playingIndex],
  );

  // 处理声音切换 - 点击时打开声音并播放视频
  const handleSoundToggle = useCallback(
    (index: number) => {
      const isMuted = mutedStates[index];

      // 如果是静音状态，则取消静音并播放视频
      if (isMuted) {
        // 更新该视频的静音状态
        setMutedStates((prev) => {
          const newStates = [...prev];
          newStates[index] = false;
          return newStates;
        });

        // 如果视频未播放，则开始播放
        if (playingIndex !== index) {
          setPlayingIndex(index);
        }
      } else {
        // 如果已经有声音，则切换为静音
        setMutedStates((prev) => {
          const newStates = [...prev];
          newStates[index] = true;
          return newStates;
        });
      }
    },
    [mutedStates, playingIndex],
  );

  return (
    <section className="w-full bg-[#F5F8FF] py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Break Language Barriers with AI Video Translation
        </h2>

        {/* 描述 */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          Instantly convert videos into 35 languages with AI voices, subtitles, and lip
          sync. Share your message globally without extra effort.
        </p>

        {/* CTA 按钮 */}
        <div className="flex justify-center mb-8 pad:mb-12">
          <StartButton />
        </div>

        {/* 视频卡片网格 */}
        <div className="grid grid-cols-1 pad:grid-cols-3 gap-1 bg-[#3366ff] rounded-2xl p-1">
          {videos.map((video, index) => (
            <VideoCard
              key={index}
              video={video}
              index={index}
              isPlaying={playingIndex === index}
              isMuted={mutedStates[index]}
              onPlayClick={() => handlePlayClick(index)}
              onSoundToggle={() => handleSoundToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default VideoShowcaseSection;
